import { apiEndpoints } from '@/apiEndpoints';
import { Receiver } from '@/types/receivers/ReceiverColumnDef';
import { DistributorParams } from './distributor.api';
import axios from 'axios';
import { ReceiverToAdd } from '@/types/receivers/ReceiverToAddColumnDef';
import { PartnershipRequest } from '@/types/receivers/PartnershipRequestColumnDef';

export interface ReceiversResponse {
  receivers: Receiver[];
  total: number;
  countRequests: number;
  countRecent: number;
  countries: string[];
}

export type ReceiversParam = DistributorParams & { onlyRecent?: boolean };

export const getReceivers = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
}: DistributorParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const result = await axios.get<ReceiversResponse>(
    apiEndpoints.companies.receivers,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
      },
    },
  );

  return result.data;
};

export interface ReceiversToAddResponse {
  allReceivers: ReceiverToAdd[];
  total: number;
  countRecent: number;
  countries: string[];
}

export const getAllReceivers = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
  onlyRecent = false,
}: ReceiversParam) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const result = await axios.get<ReceiversToAddResponse>(
    apiEndpoints.companies.allReceivers,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
        onlyRecent,
      },
    },
  );

  return result.data;
};

export const sendPartnershipRequest = async (receivers: string[]) => {
  const result = await axios.post(apiEndpoints.companies.partnershipRequest, {
    receivers,
  });
  return result.data;
};

interface PartnershipRequestsResponse {
  requests: PartnershipRequest[];
  total: number;
}

export type PartnershipRequestsParam = Omit<DistributorParams, 'filters'>;

export const findPartnershipRequests = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
}: PartnershipRequestsParam) => {
  const filtersStr = Object.entries({ page, pageSize, sortField, sortOrder })
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const result = await axios.get<PartnershipRequestsResponse>(
    apiEndpoints.companies.partnershipRequests,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
      },
    },
  );

  return result.data;
};

export const cancelPartnershipRequest = async (receiverId: string) => {
  const result = await axios.delete(
    apiEndpoints.companies.partnershipRequestDetail(receiverId),
  );

  return result.data;
};

export type PartnerReceiverInfo = {
  name: string;
  country: string | null;
  city: string | null;
  address: string | null;
  postalCode: string | null;
  contacts: {
    name: string | null;
    firstName: string | null;
    email: string | null;
    phoneNumber: string | null;
  }[];
  reservationEmails: string[];
  User: {
    firstname: string;
    lastname: string;
    email: string;
    phoneNumber: string | null;
  }[];
};

export const getPartnerReceiverInfo = async (receiverId: string) => {
  const result = await axios.get<PartnerReceiverInfo>(
    apiEndpoints.companies.company(receiverId),
  );
  return result.data;
};

export const removePartnerCompany = async (receiverId: string) => {
  const result = await axios.delete(apiEndpoints.companies.company(receiverId));
  return result;
};
